import { DataSourceConfig, DataSourceMappingConfig } from '../types/dataSource';

/**
 * Pre-seeded mapping configuration for Access Financials.
 * Demonstrates the transaction rules layer to admins and in demos.
 */
const accessFinancialsMappingConfig: DataSourceMappingConfig = {
  transactionRules: [
    {
      id: 'rule-af-001',
      label: 'Exclude intercompany invoices',
      active: true,
      action: 'exclude',
      field: 'transaction_flag',
      operator: 'is',
      value: 'intercompany',
      fromTemplate: 'intercompany',
      createdAt: '2026-01-15T09:00:00Z',
    },
    {
      id: 'rule-af-002',
      label: 'Exclude accrual journals',
      active: true,
      action: 'exclude',
      field: 'transaction_type',
      operator: 'is',
      value: 'Accrual Journal',
      fromTemplate: 'accrual_journals',
      createdAt: '2026-01-15T09:00:00Z',
    },
    {
      id: 'rule-af-003',
      label: 'Exclude depreciation entries',
      active: true,
      action: 'exclude',
      field: 'transaction_type',
      operator: 'is',
      value: 'Depreciation',
      fromTemplate: 'depreciation',
      createdAt: '2026-01-15T09:00:00Z',
    },
    {
      id: 'rule-af-004',
      label: 'Exclude suspense account movements',
      active: false,
      action: 'exclude',
      field: 'nominal_code',
      operator: 'in_range',
      value: '9900,9999',
      fromTemplate: 'suspense_accounts',
      createdAt: '2026-01-20T11:30:00Z',
    },
  ],
  lastUpdated: '2026-01-20T11:30:00Z',
};

/**
 * Demo Data Source Configuration
 * 
 * This configuration persists in git and is shared across all users.
 * Represents the 6 master datasets that are "connected" in demo mode.
 * 
 * BEHAVIOR:
 * - On app load: These 6 sources are always shown as configured
 * - During session: Users can add/edit/remove sources (React state only)
 * - On refresh: Resets back to these 6 sources (clean demo state)
 * 
 * USE CASE:
 * - Sales demos start with consistent configuration
 * - Training sessions have predictable starting point
 * - Customers see the same demo environment
 * - Developers share same configuration via git
 * 
 * TO UPDATE:
 * - Manually edit this file
 * - Commit to git
 * - All users get updated config on next pull/refresh
 */
export const demoDataSources: DataSourceConfig[] = [
  {
    id: 'demo-accounting-001',
    name: 'Access Financials',
    type: 'Access Financials',
    priority: 1, // Highest priority
    connectionDetails: {
      apiKey: '***demo-key***',
      endpoint: 'https://api.access-financials.demo/v1',
      instanceId: 'FIN-2847',
      transactionTypes: {
        salesLedger: true,
        purchaseLedger: true,
        cashbook: true
      }
    },
    status: 'connected',
    lastSync: new Date().toISOString(),
    recordCount: 1247, // From accountingData.ts: transactions, invoices, bills, GL accounts
    dateAdded: '2025-11-01T00:00:00Z',
    syncSchedule: {
      enabled: true,
      frequencyMinutes: 60, // Hourly (real-time would be event-driven, using hourly for demo)
      daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
      startTime: '06:00',
      endTime: '18:00'
    },
    parentCategory: {
      isPrimary: true,
      categoryField: 'Nominal Code - Level 3'
    },
    mappingConfig: accessFinancialsMappingConfig,
  },
  {
    id: 'demo-payroll-001',
    name: 'Paycircle',
    type: 'Paycircle',
    priority: 2,
    connectionDetails: {
      apiKey: '***demo-key***',
      endpoint: 'https://api.paycircle.demo/v1',
      instanceId: 'PAY-5678',
      transactionTypes: {
        payroll: true,
      }
    },
    status: 'connected',
    lastSync: new Date().toISOString(),
    recordCount: 158, // From payrollData.ts: payroll entries, tax obligations, employee costs
    dateAdded: '2025-11-01T00:00:00Z',
    syncSchedule: {
      enabled: true,
      frequencyMinutes: 1440, // Daily (nightly)
      daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
      startTime: '22:00',
      endTime: '23:00'
    }
  },
  {
    id: 'demo-bank-001',
    name: 'Plaid',
    type: 'Plaid',
    priority: 3,
    connectionDetails: {
      apiKey: '***demo-key***',
      endpoint: 'https://api.plaid.demo/v1',
      transactionTypes: {
        bankAccounts: true,
      }
    },
    status: 'connected',
    lastSync: new Date().toISOString(),
    recordCount: 45, // Bank transactions and account balances
    dateAdded: '2025-11-01T00:00:00Z',
    syncSchedule: {
      enabled: true,
      frequencyMinutes: 60, // Hourly
      daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
      startTime: '06:00',
      endTime: '22:00'
    }
  },
  {
    // Excel source 1 — Purchase Orders from internal spreadsheet
    // BuildRight manages POs in Excel because their ordering process is manual.
    // This is a classic "catch-all" Excel use case — no ordering system API exists.
    id: 'demo-excel-po-001',
    name: 'Purchase Orders — Internal Tracker',
    type: 'Excel Spreadsheet',
    priority: 4,
    connectionDetails: {
      pathType: 'cloud',
      cloudProvider: 'sharepoint',
      filePath: 'https://buildright.sharepoint.com/sites/finance/Shared Documents/PO-Tracker-FY2025-26.xlsx',
      hasPassword: false,
      excelConfig: {
        dateFormat: 'DD/MM/YYYY',
        amountFormat: 'negative_outflow',
        validationBehaviour: 'skip_and_flag',
        fileMode: 'cloud_file',
        worksheetConfigs: {
          purchaseOrders: { sheetName: 'Active POs', headerRow: 1, dataStartRow: 2 },
        },
        columnMappings: {
          purchaseOrders: [
            { forecasterId: 'po_number',       forecasterLabel: 'PO Number',         columnLetter: 'A', required: true },
            { forecasterId: 'supplier_name',   forecasterLabel: 'Supplier Name',      columnLetter: 'B', required: true },
            { forecasterId: 'po_date',         forecasterLabel: 'PO Date',            columnLetter: 'C', required: true },
            { forecasterId: 'expected_date',   forecasterLabel: 'Expected Delivery',  columnLetter: 'D', required: true },
            { forecasterId: 'amount',          forecasterLabel: 'Net Value',          columnLetter: 'E', required: true },
            { forecasterId: 'vat_amount',      forecasterLabel: 'VAT Amount',         columnLetter: 'F', required: false },
            { forecasterId: 'status',          forecasterLabel: 'Status',             columnLetter: 'G', required: true },
            { forecasterId: 'project_code',    forecasterLabel: 'Project Code',       columnLetter: 'H', required: false },
          ]
        }
      }
    },
    status: 'connected',
    lastSync: new Date().toISOString(),
    recordCount: 47,
    dateAdded: '2025-10-15T00:00:00Z',
    syncSchedule: {
      enabled: true,
      frequencyMinutes: 1440, // Daily
      daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
      startTime: '07:00',
      endTime: '07:00'
    }
  },
  {
    // Excel source 2 — Sales Ledger export from a legacy system with no API
    // BuildRight's legacy estimating system exports outstanding quotes/orders to Excel weekly.
    // This represents the "system with export-only capability" use case.
    id: 'demo-excel-sl-001',
    name: 'Sales Pipeline — Estimating System Export',
    type: 'Excel Spreadsheet',
    priority: 5,
    connectionDetails: {
      pathType: 'network',
      networkPath: '\\\\buildright-server\\exports\\estimating\\sales-pipeline.xlsx',
      hasPassword: false,
      transactionTypes: {
        salesOrders: true,
      },
      excelConfig: {
        dateFormat: 'DD/MM/YYYY',
        amountFormat: 'negative_outflow',
        validationBehaviour: 'skip_and_flag',
        fileMode: 'watched_folder',
        pollFrequencyMinutes: 60,
        worksheetConfigs: {
          // This file has a merged title row on row 1 — data headers on row 2
          salesOrders: { sheetName: 'Confirmed Orders', headerRow: 2, dataStartRow: 3 },
        },
        columnMappings: {
          salesOrders: [
            { forecasterId: 'so_number',       forecasterLabel: 'Order Number',       columnLetter: 'A', required: true },
            { forecasterId: 'customer_name',   forecasterLabel: 'Customer Name',       columnLetter: 'B', required: true },
            { forecasterId: 'order_date',      forecasterLabel: 'Order Date',          columnLetter: 'C', required: true },
            { forecasterId: 'expected_date',   forecasterLabel: 'Expected Invoice Date', columnLetter: 'D', required: true },
            { forecasterId: 'amount',          forecasterLabel: 'Contract Value',      columnLetter: 'E', required: true },
            { forecasterId: 'stage',           forecasterLabel: 'Stage',               columnLetter: 'F', required: false },
            { forecasterId: 'project_manager', forecasterLabel: 'Project Manager',     columnLetter: 'G', required: false },
          ]
        }
      }
    },
    status: 'connected',
    lastSync: new Date().toISOString(),
    recordCount: 23,
    dateAdded: '2025-09-01T00:00:00Z',
    syncSchedule: {
      enabled: true,
      frequencyMinutes: 60,
      daysOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri'],
      startTime: '08:00',
      endTime: '18:00'
    }
  }
];

/**
 * Get the demo data source configuration
 * 
 * This function returns a fresh copy of the demo configuration.
 * Used by DataSourceContext to initialize state.
 * 
 * @returns Array of configured data sources
 */
export const getDemoDataSources = (): DataSourceConfig[] => {
  // Return a deep copy to prevent accidental mutations
  return JSON.parse(JSON.stringify(demoDataSources));
};

/**
 * Get total record count across all demo data sources
 * 
 * @returns Total number of records from all sources
 */
export const getTotalDemoRecordCount = (): number => {
  return demoDataSources.reduce((total, source) => total + source.recordCount, 0);
};

/**
 * Get demo data sources by type
 * 
 * @param type - Data source type to filter by
 * @returns Array of data sources matching the type
 */
export const getDemoDataSourcesByType = (type: string): DataSourceConfig[] => {
  return demoDataSources.filter(source => source.type === type);
};

/**
 * Check if a demo data source exists by ID
 * 
 * @param id - Data source ID to check
 * @returns True if source exists in demo config
 */
export const isDemoDataSource = (id: string): boolean => {
  return demoDataSources.some(source => source.id === id);
};
